import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import api, { formatApiError } from "../lib/api";
import { LineMask } from "../components/marketing/Reveal";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError("Passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated — sign in with your new password");
      navigate("/login");
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-ink text-white flex flex-col items-center justify-center px-6 noise-overlay">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-12" data-testid="reset-logo">
          <span className="h-8 w-8 rounded-lg bg-neon flex items-center justify-center font-display font-black text-ink text-sm">OX</span>
          <span className="font-display font-bold text-lg tracking-tight">OrthoPlani<span className="text-neon">X</span></span>
        </Link>
        <h1 className="font-display font-black tracking-tight text-4xl sm:text-5xl leading-[1.0]">
          <LineMask delay={0.05}>Choose a new</LineMask>
          <LineMask delay={0.2} className="text-neon">password.</LineMask>
        </h1>

        {!token ? (
          <p className="mt-8 text-sm text-red-400" data-testid="reset-no-token">
            This reset link is missing its token. Request a new one from the <Link to="/forgot-password" className="text-neon underline">forgot password page</Link>.
          </p>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="reset-form">
            <div className="space-y-2">
              <Label htmlFor="r-pass" className="text-slate-300">New password</Label>
              <Input id="r-pass" data-testid="reset-password-input" type="password" required minLength={8} autoComplete="new-password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="bg-ink-2/80 border-white/15 text-white placeholder:text-slate-600 focus-visible:ring-neon h-12" placeholder="Min. 8 characters" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="r-confirm" className="text-slate-300">Confirm new password</Label>
              <Input id="r-confirm" data-testid="reset-confirm-input" type="password" required minLength={8} autoComplete="new-password"
                value={confirm} onChange={(e) => setConfirm(e.target.value)}
                className="bg-ink-2/80 border-white/15 text-white placeholder:text-slate-600 focus-visible:ring-neon h-12" placeholder="Repeat password" />
            </div>
            {error && <p className="text-sm text-red-400" data-testid="reset-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="reset-submit-btn"
              className="w-full rounded-full bg-neon text-ink font-bold hover:bg-neon/90 h-12 text-sm">
              <KeyRound className="h-4 w-4 mr-2" /> {loading ? "Updating…" : "Update password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
